import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  nome: "",
  telefone: "",
  celular: "",
  cpf: "",
  placa: "",
  modelo_veiculo: "",
  km_ultima_troca: 0,
};

export default function Clientes() {
  const { unidade, theme } = useOutletContext();
  const [lista, setLista] = useState([]);
  const [busca, setBusca] = useState("");
  const [modal, setModal] = useState(null); // {mode:'new'|'edit', data}
  const [form, setForm] = useState(emptyForm);

  const load = () =>
    api
      .get("/clientes", { params: { unidade_id: unidade.id } })
      .then((r) => setLista(r.data));

  useEffect(() => {
    load();
  }, [unidade.id]); // eslint-disable-line

  const filtrados = lista.filter((c) => {
    const q = busca.trim().toLowerCase();
    if (!q) return true;
    return (
      c.nome.toLowerCase().includes(q) ||
      c.placa.toLowerCase().includes(q) ||
      (c.modelo_veiculo || "").toLowerCase().includes(q) ||
      (c.telefone || "").includes(q) ||
      (c.celular || "").includes(q) ||
      (c.cpf || "").includes(q)
    );
  });

  const openNew = () => {
    setForm(emptyForm);
    setModal({ mode: "new" });
  };
  const openEdit = (c) => {
    setForm({
      nome: c.nome,
      telefone: c.telefone || "",
      celular: c.celular || "",
      cpf: c.cpf || "",
      placa: c.placa,
      modelo_veiculo: c.modelo_veiculo,
      km_ultima_troca: c.km_ultima_troca,
    });
    setModal({ mode: "edit", data: c });
  };

  const salvar = async () => {
    try {
      if (modal.mode === "new") {
        await api.post("/clientes", { ...form, unidade_id: unidade.id });
        toast.success("Cliente criado");
      } else {
        await api.put(`/clientes/${modal.data.id}`, form);
        toast.success("Cliente atualizado");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const excluir = async (c) => {
    if (!window.confirm(`Excluir o cliente ${c.nome}?`)) return;
    await api.delete(`/clientes/${c.id}`);
    toast.success("Cliente excluído");
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Base da unidade
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Clientes
          </h2>
        </div>
        <button
          data-testid="cliente-novo-btn"
          onClick={openNew}
          className={`${theme.primary} h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal`}
        >
          <Plus className="w-4 h-4" /> Novo
        </button>
      </div>

      <div className="bg-white border-2 border-neutral-900 mb-0">
        <div className="p-3 border-b-2 border-neutral-900 relative">
          <Search className="w-4 h-4 absolute left-5 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            data-testid="cliente-busca-input"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, placa, telefone..."
            className="w-full h-10 pl-8 pr-3 font-mono text-sm focus:outline-none"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-300 bg-neutral-50">
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Nome</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Placa</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden sm:table-cell">Modelo</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">Celular</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden lg:table-cell">CPF</th>
                <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">KM</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c) => (
                <tr key={c.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="p-3 font-display font-bold">{c.nome}</td>
                  <td className="p-3 font-mono text-sm">{c.placa}</td>
                  <td className="p-3 text-sm hidden sm:table-cell">{c.modelo_veiculo}</td>
                  <td className="p-3 font-mono text-xs hidden md:table-cell">{c.celular || c.telefone || "—"}</td>
                  <td className="p-3 font-mono text-xs hidden lg:table-cell">{c.cpf || "—"}</td>
                  <td className="p-3 text-right font-mono text-sm">
                    {Number(c.km_ultima_troca || 0).toLocaleString("pt-BR")}
                  </td>
                  <td className="p-3 flex gap-1 justify-end">
                    <button
                      data-testid={`cliente-editar-${c.id}`}
                      onClick={() => openEdit(c)}
                      className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`cliente-excluir-${c.id}`}
                      onClick={() => excluir(c)}
                      className="p-2 border border-neutral-900 hover:bg-red-600 hover:text-white hover:border-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-neutral-500">
                    Nenhum cliente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900">
              <h3 className="font-display font-bold uppercase">
                {modal.mode === "new" ? "Novo Cliente" : "Editar Cliente"}
              </h3>
              <button onClick={() => setModal(null)} className="p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <Input label="Nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testid="form-nome" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Celular" value={form.celular} onChange={(v) => setForm({ ...form, celular: v.replace(/[^0-9]/g, "") })} testid="form-celular" />
                <Input label="Telefone fixo" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} testid="form-telefone" />
              </div>
              <Input label="CPF" value={form.cpf} onChange={(v) => setForm({ ...form, cpf: v.replace(/[^0-9]/g, "").slice(0, 11) })} testid="form-cpf" />
              <Input
                label="Placa"
                value={form.placa}
                onChange={(v) => setForm({ ...form, placa: v.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                testid="form-placa"
              />
              <Input label="Modelo do Veículo" value={form.modelo_veiculo} onChange={(v) => setForm({ ...form, modelo_veiculo: v })} testid="form-modelo" />
              <Input
                label="KM Última Troca"
                type="number"
                value={form.km_ultima_troca}
                onChange={(v) => setForm({ ...form, km_ultima_troca: Number(v) })}
                testid="form-km"
              />
            </div>
            <div className="p-4 border-t-2 border-neutral-900 flex gap-2 justify-end">
              <button
                onClick={() => setModal(null)}
                className="h-10 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest"
              >
                Cancelar
              </button>
              <button
                data-testid="form-salvar-btn"
                onClick={salvar}
                className={`${theme.primary} h-10 px-5 font-display font-bold uppercase text-xs tracking-widest`}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
      />
    </label>
  );
}
